import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusCircle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { stockTransformationService } from "@/services/stock-transformation";

import { RepackShrinkageTab } from "./repack-shrinkage-tab";
import { RepackingCancelDialog } from "./repacking-cancel-dialog";
import { RepackingDetailDialog } from "./repacking-detail-dialog";
import { RepackingDialog } from "./repacking-dialog";
import { RepackingSummaryCards } from "./repacking-summary-cards";
import { RepackingTableCard } from "./repacking-table-card";
import { summarizeRepacking } from "./repacking-utils";

import type { StockTransformation } from "@artisancode/api-types";

export function RepackingList() {
  const queryClient = useQueryClient();

  // Active view tab
  const [activeTab, setActiveTab] = useState<"list" | "shrinkage">("list");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [detailTransformation, setDetailTransformation] =
    useState<StockTransformation | null>(null);
  const [cancellingTransformation, setCancellingTransformation] =
    useState<StockTransformation | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);

  // Fetch transformations
  const {
    data: listRes,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["stock-transformations", "repack"],
    queryFn: () =>
      stockTransformationService.list({ type: "repack", per_page: 200 }),
  });

  const transformations = listRes?.items || [];

  // Metrics
  const totalCount = transformations.length;
  const completedTransformations = transformations.filter(
    (t) => t.status === "completed",
  );
  const completedCount = completedTransformations.length;
  const draftCount = transformations.filter((t) => t.status === "draft").length;
  const totalShrinkageValue = completedTransformations.reduce(
    (sum, t) => sum + summarizeRepacking(t).shrinkageValue,
    0,
  );

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: (id: string) => stockTransformationService.complete(id),
    onSuccess: (data) => {
      toast.success(
        `Repacking ${data.code} berhasil diselesaikan. Stok dipindahkan ke wadah repacking bar!`,
      );
      queryClient.invalidateQueries({ queryKey: ["stock-transformations"] });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      if (detailTransformation?.id === data.id) {
        setDetailTransformation(data);
      }
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      stockTransformationService.cancel(id, { cancelReason: reason }),
    onSuccess: () => {
      toast.success("Dokumen repacking berhasil dibatalkan.");
      queryClient.invalidateQueries({ queryKey: ["stock-transformations"] });
      queryClient.invalidateQueries({ queryKey: ["stocks"] });
      setCancellingTransformation(null);
      setDetailTransformation(null);
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Repacking Bahan Baku"
        description="Pemecahan kemasan bulk/segel (on-hand) ke wadah operasional bar (repacking) serta pencatatan susut timbangan."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
              />
              Muat Ulang
            </Button>

            <Button
              size="sm"
              onClick={() => setIsCreateOpen(true)}
              className="min-h-[40px] sm:min-h-0 sm:h-9 gap-1.5 text-xs shadow-xs"
            >
              <PlusCircle className="h-4 w-4" />
              Buka Kemasan / Repack
            </Button>
          </div>
        }
      />

      <div className="inline-flex rounded-lg bg-muted p-1 text-xs">
        <button
          type="button"
          onClick={() => setActiveTab("list")}
          className={cn(
            "rounded-md px-3 py-1.5 font-medium transition-all cursor-pointer",
            activeTab === "list"
              ? "bg-background text-foreground shadow-xs font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Daftar Repacking
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("shrinkage")}
          className={cn(
            "rounded-md px-3 py-1.5 font-medium transition-all cursor-pointer",
            activeTab === "shrinkage"
              ? "bg-background text-foreground shadow-xs font-semibold"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          Laporan Susut
        </button>
      </div>

      {activeTab === "list" ? (
        <>
          <RepackingSummaryCards
            totalCount={totalCount}
            completedCount={completedCount}
            draftCount={draftCount}
            totalShrinkageValue={totalShrinkageValue}
          />

          <RepackingTableCard
            transformations={transformations}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onViewDetail={setDetailTransformation}
            onComplete={setCompletingId}
            onCancelRequest={setCancellingTransformation}
            isCompleting={completeMutation.isPending}
          />
        </>
      ) : (
        <RepackShrinkageTab />
      )}

      {/* Dialogs */}
      <RepackingDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["stock-transformations"],
          });
          queryClient.invalidateQueries({ queryKey: ["stocks"] });
        }}
      />

      <RepackingDetailDialog
        transformation={detailTransformation}
        open={!!detailTransformation}
        onOpenChange={(open) => {
          if (!open) setDetailTransformation(null);
        }}
        onComplete={setCompletingId}
        onCancelRequest={(trans) => setCancellingTransformation(trans)}
        isCompleting={completeMutation.isPending}
      />

      <RepackingCancelDialog
        transformation={cancellingTransformation}
        open={!!cancellingTransformation}
        onOpenChange={(open) => {
          if (!open) setCancellingTransformation(null);
        }}
        onConfirm={async (id, reason) => {
          await cancelMutation.mutateAsync({ id, reason });
        }}
        isSubmitting={cancelMutation.isPending}
      />

      <ConfirmDialog
        open={!!completingId}
        onOpenChange={(open) => {
          if (!open) setCompletingId(null);
        }}
        title="Selesaikan repacking?"
        description="Stok dipindahkan dari barang sumber ke wadah repacking di bar dan tercatat di Kartu Stok."
        confirmLabel="Ya, Selesaikan"
        variant="default"
        isConfirming={completeMutation.isPending}
        onConfirm={() => {
          if (!completingId) return;
          completeMutation.mutate(completingId, {
            onSettled: () => setCompletingId(null),
          });
        }}
      />
    </div>
  );
}
